if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this._classes = {
          loaded: 'quick-view-loaded',
        };
        this.drawerBody = this.querySelector(this.selector);
      }

      get selector() {
        return '.quick-view__content';
      }

      get sourceSelector() {
        return '#MainProduct-quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      shouleBeShow() {
        const sectionId = this.getProductQuickViewSectionId();
        return typeof sectionId === 'string';
      }

      show(opener) {
        // Capture the color the shopper selected on the product card (set by
        // product-card-variant-images.js) so it can be pre-selected once the
        // quick-view content loads. Size is never carried over.
        this._preselectColor = null;
        if (opener && typeof opener.closest === 'function') {
          const card = opener.closest('[data-prod-card]');
          if (card && card.dataset.selectedColor) {
            this._preselectColor = card.dataset.selectedColor;
          }
        }
        super.show(opener);
      }

      prepareToShow() {
        super.prepareToShow();
        this.quickview();
      }

      // Pre-select the carried-over color in the freshly loaded variant picker.
      // Only the color option is targeted; size is left for the shopper.
      //
      // The size fieldset starts `.is-locked` (pointer-events:none) until a
      // color is chosen — product-info unlocks it inside its color `change`
      // handler (which also refreshes per-color size availability and clears any
      // stale size selection). That handler's color branch does NOT refetch the
      // section, so dispatching `change` is safe and is in fact REQUIRED: without
      // it the size fieldset stays locked and sizes can't be tapped.
      applyPreselectedColor() {
        if (!this._preselectColor) return;
        const norm = (s) => (s || '').trim().toLowerCase();
        const want = norm(this._preselectColor);
        let tries = 0;
        const MAX_TRIES = 40; // ~4s at 100ms
        const tryApply = () => {
          const content = this.querySelector(this.selector);
          const productInfo = content && content.querySelector('product-info');
          const variantSelects = content && content.querySelector('variant-selects');
          const inputs = content
            ? content.querySelectorAll(
                '.product-form__input--color input[type="radio"], .product-form__input--swatch input[type="radio"]'
              )
            : [];
          const target = Array.from(inputs).find((i) => norm(i.value) === want);
          // The injected markup + product-info custom element must be FULLY
          // present before clicking: product-info's color handler reads
          // [data-selected-swatch-value] and the size fieldset, and throws (so
          // it never removes .is-locked) if those aren't rendered yet. Poll
          // until all of them exist, then drive the native selection path.
          //
          // CRITICAL: querySelector matches custom elements even before they are
          // UPGRADED, so element existence is not enough. variant-selects (whose
          // `change` listener publishes the pubsub event) and product-info (which
          // subscribes to it and unlocks the size fieldset) only attach those
          // handlers in their connectedCallback — i.e. once `:defined`. Clicking
          // the color before both are upgraded fires a native `change` that NO
          // ONE listens to: no pubsub publish, color handler never runs, size
          // stays .is-locked. Require both to be `:defined` before clicking.
          const ready =
            target &&
            productInfo &&
            productInfo.matches(':defined') &&
            variantSelects &&
            variantSelects.matches(':defined') &&
            content.querySelector('[data-selected-swatch-value]') &&
            content.querySelector('[data-field-option="size"]');
          if (!ready && tries < MAX_TRIES) {
            tries += 1;
            setTimeout(tryApply, 100);
            return;
          }
          if (!target) return;
          // Click the color's LABEL, not the input — this drives the theme's
          // native selection path (label → input change → product-info color
          // handler), which unlocks the size fieldset (removes .is-locked) and
          // refreshes per-color size availability so sizes become selectable.
          //
          // The carried color is often ALREADY the server-rendered default
          // (`target.checked === true`). product-info's connectedCallback only
          // strips the `checked` ATTRIBUTE (not the property), so a plain
          // label.click() on an already-checked radio fires NO `change`, the
          // color handler never runs, and the size fieldset stays `.is-locked`.
          // Force a real state transition first so `change` always fires.
          if (target.checked) target.checked = false;
          const label =
            (target.id && content.querySelector(`label[for="${CSS.escape(target.id)}"]`)) ||
            target.closest('label') ||
            target.nextElementSibling;
          if (label && typeof label.click === 'function') {
            label.click();
          } else {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };
        tryApply();
      }

      handleAfterShow() {
        super.handleAfterShow();
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      }

      handleAfterHide() {
        super.handleAfterHide();
        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.remove(this._classes.loaded);
      }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }

          // Cache for better performance.
          FoxTheme.QuickViewSectionId = sectionId;
        }

        return sectionId;
      }

      quickview() {
        const drawerContent = this.querySelector(this.selector);
        const sectionId = this.getProductQuickViewSectionId();
        const sectionUrl = `${this.dataset.productUrl.split('?')[0]}?section_id=${sectionId}`;
        fetch(sectionUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const productElement = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.sourceSelector);

            this.setInnerHTML(drawerContent, productElement.content.cloneNode(true));
            this.applyPreselectedColor();
            FoxTheme.a11y.trapFocus(this, this.focusElement);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            setTimeout(() => {
              this.classList.add(this._classes.loaded);
            }, 300);

            document.dispatchEvent(
              new CustomEvent('quick-view:loaded', {
                detail: { productUrl: this.dataset.productUrl },
              })
            );
          })
          .catch((e) => {
            console.error(e);
          });
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = '';
        element.appendChild(innerHTML);
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.handleAfterHide();
      }
    }
  );
}
